import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { SectionSeparator } from '../../ui/TableDetailSplitLayout';
import { AppButton } from '../../design-system';
import type { DeviceInventoryStatus, EfemGatewayLoadPortConfig, TenantDeviceRecord } from '@cnapse/shared/schemas.js';
import * as dt from '@cnapse/shared/data.types.js';
import { PRODUCT_NAMES, buildTenantEfemGatewayStatusMap } from '@cnapse/shared';
import { providerEfemService } from '../../../services/ProviderEfemService';
import { tenantDevicesService } from '../../../services/TenantDevicesService';
import { efemService } from '../../../services/EfemService';
import { draftsEqual, saveEfemGatewaySetup } from './efemGatewaySetupSave';
import type { EfemGatewaySetupDraft, LoadPortRow, WaferCameraEditRow } from './efemGatewaySetup.types';
import { GatewayInlineNameEdit } from './GatewayInlineNameEdit';
import { LoadPortsSection } from './LoadPortsSection';
import { HandheldScannerSection } from './HandheldScannerSection';
import { WaferIdCameraSection } from './WaferIdCameraSection';
import type { WaferCameraPeripheralRole } from './types';

interface EfemGatewayDetailProps {
  gatewaySerialNumber: string;
  apiMode: 'tenant' | 'provider';
  tenantId?: string;
  isUnregistered?: boolean;
  inventoryStatus?: DeviceInventoryStatus;
  /** When false, hide tenant-scoped handheld setup (e.g. gateway not assigned to a tenant on provider). */
  tenantAllocated?: boolean;
  onRefresh?: () => void;
}

const INVENTORY_STATUS_LABELS: Record<DeviceInventoryStatus, string> = {
  'in-stock': 'In Stock',
  allocated: 'Allocated',
  shipped: 'Shipped',
  claimed: 'Claimed',
  active: 'Active',
  returned: 'Returned',
  retired: 'Retired',
};

function AwaitingActivationPanel({
  gatewaySerialNumber,
  inventoryStatus,
}: {
  gatewaySerialNumber: string;
  inventoryStatus: DeviceInventoryStatus;
}) {
  const statusLabel = INVENTORY_STATUS_LABELS[inventoryStatus] ?? inventoryStatus;

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Awaiting gateway activation
      </Typography>
      <Alert severity="info">
        This gateway ({gatewaySerialNumber}) is in inventory status &quot;{statusLabel}&quot; and will be
        available for Axon setup once activated.
      </Alert>
    </Paper>
  );
}

function parseRoleKeyFallback(roleKey: string): { supportedWaferSize: dt.WAFER_SIZE; idSide: 'top' | 'bottom' } {
  const [sizePart, sidePart] = roleKey.split('-');
  const supportedWaferSize = Object.values(dt.WAFER_SIZE).includes(sizePart as dt.WAFER_SIZE)
    ? (sizePart as dt.WAFER_SIZE)
    : dt.WAFER_SIZE.Size300mm;
  const idSide = sidePart === 'bottom' ? 'bottom' : 'top';
  return { supportedWaferSize, idSide };
}

function roleToRow(role: WaferCameraPeripheralRole): WaferCameraEditRow {
  const fallback = parseRoleKeyFallback(role.roleKey);
  return {
    roleKey: role.roleKey,
    roleLabel: role.roleLabel,
    boundSerial: role.boundSerialNumber ?? '',
    displayLabel: role.displayLabel ?? '',
    idSide: role.idSide ?? fallback.idSide,
    mountCalibrationDeg: role.mountCalibrationDeg ?? 0,
    supportedWaferSize:
      (role.supportedWaferSize as dt.WAFER_SIZE | undefined) ?? fallback.supportedWaferSize,
    configured: role.configured,
  };
}

function InitializePanel({
  gatewaySerialNumber,
  onDone,
}: {
  gatewaySerialNumber: string;
  onDone: () => void;
}) {
  const [name, setName] = useState(gatewaySerialNumber);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInit = async () => {
    setSaving(true);
    setError(null);
    try {
      await efemService.createEfemClient({ name, serialNumber: gatewaySerialNumber });
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : `Failed to initialize ${PRODUCT_NAMES.AXON} client`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Initialize gateway
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        This gateway has been claimed as a device but hasn&apos;t been initialized as a {PRODUCT_NAMES.AXON} client yet.
        Set a friendly name and click <strong>Initialize</strong> to enable full Axon setup.
      </Alert>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
        <GatewayInlineNameEdit
          name={name}
          serialNumber={gatewaySerialNumber}
          onChange={setName}
        />
        <AppButton
          variant="contained"
          onClick={() => void handleInit()}
          disabled={saving || !name.trim()}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : 'Initialize'}
        </AppButton>
      </Box>
    </Paper>
  );
}

export function EfemGatewayDetail({
  gatewaySerialNumber,
  apiMode,
  tenantId,
  isUnregistered,
  inventoryStatus = 'in-stock',
  tenantAllocated = apiMode === 'tenant',
  onRefresh,
}: EfemGatewayDetailProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [baseline, setBaseline] = useState<EfemGatewaySetupDraft | null>(null);
  const [draft, setDraft] = useState<EfemGatewaySetupDraft | null>(null);
  const [allScanners, setAllScanners] = useState<TenantDeviceRecord[]>([]);
  const [allHandheldScanners, setAllHandheldScanners] = useState<TenantDeviceRecord[]>([]);
  const [allCameras, setAllCameras] = useState<TenantDeviceRecord[]>([]);
  const [tenantFleetDevices, setTenantFleetDevices] = useState<TenantDeviceRecord[]>([]);

  const tenantGatewayStatusBySerial = useMemo(
    () => buildTenantEfemGatewayStatusMap(tenantFleetDevices),
    [tenantFleetDevices],
  );

  const effectiveTenantId = apiMode === 'provider' ? tenantId : undefined;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [clientsRes, devicesRes, profile, matrix] = await Promise.all([
        apiMode === 'provider'
          ? providerEfemService.getProviderEfemClients(tenantId!)
          : efemService.getEfemClients(),
        apiMode === 'provider'
          ? providerEfemService.getProviderTenantDevices(tenantId!)
          : tenantDevicesService.getTenantDevices(),
        effectiveTenantId
          ? providerEfemService.getProviderEfemHardwareProfile(tenantId!, gatewaySerialNumber)
          : efemService.getEfemHardwareProfile(gatewaySerialNumber),
        effectiveTenantId
          ? providerEfemService.getProviderEfemPeripheralMatrix(tenantId!, gatewaySerialNumber)
          : efemService.getEfemPeripheralMatrix(gatewaySerialNumber),
      ]);

      const client = clientsRes.clients.find((c) => c.gatewaySerialNumber === gatewaySerialNumber);
      const devices = devicesRes.data.devices;

      setTenantFleetDevices(devices);
      setAllScanners(devices.filter((d) => d.deviceCategory === 'barcode-scanner'));
      setAllHandheldScanners(devices.filter((d) => d.deviceCategory === 'handheld-barcode-scanner'));
      setAllCameras(devices.filter((d) => d.deviceCategory === 'waferid-camera'));

      const handheldSlot = matrix.handheldBarcodeScanner;

      const loadPorts: LoadPortRow[] = profile.hardwareProfile?.loadPorts?.length
        ? profile.hardwareProfile.loadPorts.map((p: EfemGatewayLoadPortConfig) => {
            const slot = matrix.barcodeScanners.find((s) => s.roleKey === `lp-${p.id}`);
            return { id: String(p.id), size: p.size, scannerSerial: slot?.boundSerialNumber ?? '' };
          })
        : [];

      const cameras = matrix.waferCameras.filter((r) => r.configured).map(roleToRow);

      const snapshot: EfemGatewaySetupDraft = {
        name: client?.name ?? '',
        loadPorts,
        handheld: {
          boundSerial: handheldSlot?.boundSerialNumber ?? '',
          displayLabel: handheldSlot?.displayLabel ?? '',
        },
        cameras,
      };

      setBaseline(snapshot);
      setDraft(snapshot);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load gateway setup');
    } finally {
      setLoading(false);
    }
  }, [gatewaySerialNumber, apiMode, tenantId, effectiveTenantId]);

  useEffect(() => {
    if (!isUnregistered) void load();
  }, [load, isUnregistered]);

  const isDirty = useMemo(
    () => baseline != null && draft != null && !draftsEqual(baseline, draft),
    [baseline, draft],
  );

  const hasName = !!draft?.name.trim();

  const showLoadPortsSection = allScanners.length > 0;
  const showHandheldSection =
    allHandheldScanners.length > 0 && (apiMode === 'tenant' || tenantAllocated);
  const showCameraSection = allCameras.length > 0;

  const handleCancel = () => {
    if (baseline) {
      setDraft({
        ...baseline,
        loadPorts: [...baseline.loadPorts],
        handheld: { ...baseline.handheld },
        cameras: [...baseline.cameras],
      });
    }
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!draft || !baseline) return;
    setSaving(true);
    setSaveError(null);
    try {
      await saveEfemGatewaySetup({
        gatewaySerialNumber,
        tenantId: effectiveTenantId,
        draft,
        baseline,
      });
      await load();
      onRefresh?.();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Failed to save gateway setup');
    } finally {
      setSaving(false);
    }
  };

  if (isUnregistered) {
    if (inventoryStatus !== 'active') {
      return (
        <AwaitingActivationPanel
          gatewaySerialNumber={gatewaySerialNumber}
          inventoryStatus={inventoryStatus}
        />
      );
    }
    return (
      <InitializePanel
        gatewaySerialNumber={gatewaySerialNumber}
        onDone={() => { onRefresh?.(); }}
      />
    );
  }

  if (loading || !draft) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box display="flex" flexDirection="column" minHeight="100%">
      <Box sx={{ mb: 2 }}>
        <GatewayInlineNameEdit
          name={draft.name}
          serialNumber={gatewaySerialNumber}
          onChange={(name) => setDraft((prev) => (prev ? { ...prev, name } : prev))}
        />
      </Box>

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      <Box sx={{ flex: 1 }}>
        {(showLoadPortsSection || showHandheldSection || showCameraSection) && (
          <Stack spacing={3} divider={<SectionSeparator />}>
            {showLoadPortsSection && (
              <LoadPortsSection
                gatewaySerialNumber={gatewaySerialNumber}
                rows={draft.loadPorts}
                onRowsChange={(loadPorts) => setDraft((prev) => (prev ? { ...prev, loadPorts } : prev))}
                allScanners={allScanners}
                tenantGatewayStatusBySerial={tenantGatewayStatusBySerial}
                disabled={!hasName}
                loading={loading}
              />
            )}

            {showHandheldSection && (
              <HandheldScannerSection
                gatewaySerialNumber={gatewaySerialNumber}
                row={draft.handheld}
                onRowChange={(handheld) => setDraft((prev) => (prev ? { ...prev, handheld } : prev))}
                allHandheldScanners={allHandheldScanners}
                tenantGatewayStatusBySerial={tenantGatewayStatusBySerial}
                disabled={!hasName}
                loading={loading}
              />
            )}

            {showCameraSection && (
              <WaferIdCameraSection
                rows={draft.cameras}
                onRowsChange={(cameras) => setDraft((prev) => (prev ? { ...prev, cameras } : prev))}
                allCameras={allCameras}
                gatewaySerialNumber={gatewaySerialNumber}
                tenantGatewayStatusBySerial={tenantGatewayStatusBySerial}
                disabled={!hasName}
                loading={loading}
              />
            )}
          </Stack>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          pt: 2,
          mt: 2,
          borderTop: 1,
          borderColor: 'divider',
          position: 'sticky',
          bottom: 0,
          zIndex: 1,
        }}
      >
        <AppButton onClick={handleCancel} disabled={!isDirty || saving}>
          Cancel
        </AppButton>
        <AppButton
          variant="contained"
          onClick={() => void handleSave()}
          disabled={!isDirty || saving || !hasName}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Save
        </AppButton>
      </Box>
    </Box>
  );
}
